import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, BookOpen, Play, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useActivities } from '@/hooks/useActivities';
import { usePlayerQuizzes, useQuizAttempt } from '@/hooks/usePlayerQuizzes';
import { Quiz } from '@/types/quiz';
import { useState } from 'react';

import { ActivityCard } from '@/components/player-program/ActivityCard';
import { ProgramInfoCard } from '@/components/player-program/ProgramInfoCard';
import { PlayerProgramHeader } from '@/components/player-program/PlayerProgramHeader';
import { VideoModalManager } from '@/components/player-program/VideoModalManager';
import { QuizCard } from '@/components/player-program/QuizCard';
import { QuizInterface } from '@/components/player-program/QuizInterface';

const PlayerProgram = () => {
  const { playerId, programId } = useParams<{ 
    playerId: string; 
    programId: string; 
  }>();
  const navigate = useNavigate();
  
  // Quiz state
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [showQuizInterface, setShowQuizInterface] = useState(false);
  

  const { data: playerProgram, isLoading: programLoading } = useQuery({
    queryKey: ['player-program', playerId, programId],
    queryFn: async () => {
      if (!playerId || !programId) {
        return null;
      }
      const { data, error } = await supabase
        .from('player_programs')
        .select(`
          *,
          program:programs(*),
          player:players(
            *,
            profile:profiles(*)
          )
        `)
        .eq('player_id', playerId)
        .eq('program_id', programId)
        .single();

      if (error) {
        console.error('❌ PlayerProgram database error:', error);
        throw error;
      }
      
      return data;
    },
    enabled: !!playerId && !!programId,
  });

  const { data: activities, isLoading: activitiesLoading, error: activitiesError } = useActivities(programId);
  
  // Quiz hooks
  const { quizzes, loading: quizzesLoading } = usePlayerQuizzes(programId, playerId);
  const { 
    currentAttempt, 
    questions, 
    answers, 
    loading: quizLoading,
    startQuizAttempt, 
    saveAnswer, 
    submitQuiz 
  } = useQuizAttempt(playerId);
  

  const handleBack = () => {
    navigate(`/player/${playerId}/programs`);
  };

  const handleTakeQuiz = async (quiz: Quiz) => {
    setSelectedQuiz(quiz);
    const attempt = await startQuizAttempt(quiz.id);
    if (attempt) {
      setShowQuizInterface(true);
    }
  };

  const handleQuizSubmit = async () => {
    const completedAttempt = await submitQuiz();
    if (completedAttempt && selectedQuiz) {
      setShowQuizInterface(false);
      setSelectedQuiz(null);
      // Navigate to dedicated results page
      navigate(`/player/${playerId}/programs/${programId}/quiz/${selectedQuiz.id}/results/${completedAttempt.id}`);
    }
  };

  const handleCloseQuizInterface = () => {
    setShowQuizInterface(false);
    setSelectedQuiz(null);
  };

  if (programLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-lg text-muted-foreground">Loading program...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!playerProgram) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="text-center py-16">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Program Not Found</h2>
              <p className="text-gray-600 mb-4">The program assignment could not be found.</p>
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <PlayerProgramHeader onBack={handleBack} />
        <ProgramInfoCard 
          playerProgram={playerProgram} 
          quizzes={quizzes}
          quizzesLoading={quizzesLoading}
          onTakeQuiz={handleTakeQuiz}
        />

        <VideoModalManager>
          {(handleWatchVideo) => (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Program Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activitiesLoading ? (
                  <div className="flex items-center gap-3 justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="text-muted-foreground">Loading activities...</span>
                  </div>
                ) : activitiesError ? (
                  <div className="text-center py-12">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <p className="text-red-800 font-medium mb-2">Error Loading Activities</p>
                      <p className="text-red-600 text-sm">{(activitiesError as Error)?.message || 'Unknown error'}</p>
                    </div>
                  </div>
                ) : !activities || activities.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Activities Found</p>
                    <p>This program doesn't have any activities yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity, index) => (
                      <ActivityCard
                        key={activity.id}
                        activity={activity}
                        index={index}
                        onWatchVideo={handleWatchVideo}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </VideoModalManager>

        {/* Quiz Interface */}
        {selectedQuiz && (
          <QuizInterface
            isOpen={showQuizInterface}
            onClose={handleCloseQuizInterface}
            quiz={selectedQuiz}
            attempt={currentAttempt}
            questions={questions}
            answers={answers}
            onSaveAnswer={saveAnswer}
            onSubmitQuiz={handleQuizSubmit}
            loading={quizLoading}
          />
        )}
      </div>
    </div>
  );
};

export default PlayerProgram;